import { useEffect, useMemo, useState } from 'react';
import { api } from './api';
import { BucketBars, CountByDay, SERIES_COLORS, ShareDonut } from './charts';
import { useCachedState, useRefreshVersion } from './store';
import { Badge, Empty, ErrorState, Metric, Select, ago, date, duration, money, shortNumber, titleCase } from './ui';
import { Flag } from './Users';

const RANGES = [['7', 'Last 7 days'], ['30', 'Last 30 days'], ['90', 'Last 90 days'], ['all', 'All time']];
const PAGE_SIZE = 25;
const PLAN_NAMES = { pro: 'Pro', studio: 'Studio' };
const LANGUAGE_SHORT = { english: 'EN', arabic: 'AR' };
const STATE_TONES = { ready: 'good', failed: 'danger', generating: 'warn' };

/** How a clip or a script write was paid for, in the customer's terms. */
const FUNDING = {
  free: ['Free weekly', 'muted'],
  plan: ['Plan minutes', 'info'],
  credits: ['Credits', 'good'],
  mixed: ['Free + credits', 'good'],
  none: ['Not charged', 'muted'],
};
const FundingBadge = ({ funding }) => { const [label, tone] = FUNDING[funding] ?? [titleCase(funding), 'muted']; return <Badge tone={tone}>{label}</Badge>; };

const minutes = (seconds) => {
  const s = Number(seconds ?? 0);
  if (!s) return '0 min';
  return s < 60 ? `${Math.round(s)} s` : `${(s / 60).toFixed(s < 600 ? 1 : 0)} min`;
};

/** Same convention as Videos: a preset range is computed at request time. */
const rangeQuery = (range) => ({
  from: new Date(range === 'all' ? '2024-01-01T00:00:00Z' : Date.now() - Number(range) * 24 * 60 * 60 * 1000).toISOString(),
  to: new Date().toISOString(),
});

/**
 * AI voiceovers and the script writer: who used them, how much, how it was
 * paid for, and what Groq billed us. Usage comes from the customer-facing
 * tables; cost from the cost ledger, so the two can be compared directly.
 */
export function Voiceovers({ onOpenVideos }) {
  const [range, setRange] = useState('30');
  const query = useMemo(() => rangeQuery(range), [range]);
  const statsKey = `/voiceovers/stats?${new URLSearchParams(query)}`;
  const [stats, setStats] = useCachedState(statsKey);
  const [loading, setLoading] = useState(!stats);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [customer, setCustomer] = useState(null);
  const refreshVersion = useRefreshVersion();

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError('');
    api(statsKey).then((next) => { if (!cancelled) setStats(next); })
      .catch((caught) => { if (!cancelled) setError(caught.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [statsKey, refreshVersion, attempt]); // eslint-disable-line react-hooks/exhaustive-deps

  const t = stats?.totals; const sc = stats?.scripts; const cost = stats?.cost;
  const paidSlices = useMemo(() => [
    { key: 'free', label: 'Free weekly minutes', value: Math.round((t?.secondsFree ?? 0) / 6) / 10, color: SERIES_COLORS[0] },
    { key: 'plan', label: 'Plan minutes', value: Math.round((t?.secondsPlan ?? 0) / 6) / 10, color: SERIES_COLORS[1] },
    { key: 'credits', label: 'Paid with credits', value: Math.round((t?.secondsCredits ?? 0) / 6) / 10, color: SERIES_COLORS[2] },
  ], [t]);
  const voiceRows = useMemo(() => (stats?.voices ?? []).map((v) => ({
    key: `${v.voice}-${v.language}`, label: `${titleCase(v.voice)} · ${LANGUAGE_SHORT[v.language] ?? v.language}`,
    title: `${titleCase(v.voice)} (${v.language}) — ${minutes(v.seconds)}`, value: v.voiceovers,
  })), [stats]);
  const toneRows = useMemo(() => (stats?.tones ?? []).map((row) => ({
    key: row.tone, label: row.tone === 'unknown' ? 'Not recorded' : titleCase(row.tone),
    title: row.tone === 'unknown' ? 'Written before tone was recorded' : titleCase(row.tone), value: row.uses,
  })), [stats]);
  const scriptFundingRows = useMemo(() => [
    { key: 'free', label: 'Free weekly', value: sc?.free ?? 0 },
    { key: 'plan', label: 'Plan', value: sc?.plan ?? 0 },
    { key: 'credits', label: 'Credits', value: sc?.paid ?? 0 },
    { key: 'refunded', label: 'Refunded', title: 'The model returned nothing — handed back', value: sc?.refunded ?? 0 },
  ], [sc]);
  const groqUsd = (cost?.ttsUsd ?? 0) + (cost?.scriptUsd ?? 0);

  return <>
    <section className="page-title"><div><div className="eyebrow">Voiceovers</div><h1>AI voiceovers</h1><p>Text to speech and the AI script writer — who uses them, how much, how they pay, and what Groq bills us.</p></div>
      <div className="btn-row"><Select value={range} onChange={(e) => { setRange(e.target.value); setCustomer(null); }} options={RANGES} label="Date range" /></div></section>

    {error && <ErrorState message={error} retry={() => setAttempt((n) => n + 1)} />}

    <section className={`metric-grid ${loading ? 'is-loading' : ''}`}>
      <Metric label="Voiceovers" value={shortNumber(t?.total)} detail={`${t?.ready ?? 0} ready · ${t?.failed ?? 0} failed${t?.generating ? ` · ${t.generating} generating` : ''} · ${t?.users ?? 0} customer${t?.users === 1 ? '' : 's'}`} alert={(t?.failed ?? 0) > 0} />
      <Metric label="Voice generated" value={minutes(t?.seconds)} detail={`English ${minutes(t?.secondsEnglish)} · Arabic ${minutes(t?.secondsArabic)}`} />
      <Metric label="Credits earned" value={shortNumber((t?.credits ?? 0) + (sc?.credits ?? 0))} detail={`Voice ${t?.credits ?? 0} · Script writer ${sc?.credits ?? 0} · free ${minutes(t?.secondsFree)} · plan ${minutes(t?.secondsPlan)}`} />
      <Metric label="Script writes" value={shortNumber(sc?.total)} detail={`${sc?.free ?? 0} free · ${sc?.plan ?? 0} plan · ${sc?.paid ?? 0} paid${sc?.refunded ? ` · ${sc.refunded} refunded` : ''} · ${t?.fromScriptWriter ?? 0} voiceovers from an idea`} />
      <Metric label="Groq cost (estimated)" value={money(groqUsd)} detail={`Voice ${money(cost?.ttsUsd)} for ${shortNumber(cost?.ttsChars)} chars${cost?.ttsFailedUsd ? ` (${money(cost.ttsFailedUsd)} on failures)` : ''} · Script ${money(cost?.scriptUsd)}`} />
      <Metric label="Used on videos" value={shortNumber(t?.onVideos)} detail={`${t?.exportedVideos ?? 0} exported with the voiceover · ${t?.deleted ?? 0} deleted by customers`} />
    </section>

    <section className={`chart-grid customer-charts ${loading ? 'is-loading' : ''}`}>
      <article className="panel"><div className="panel-heading"><div><span className="eyebrow">VOLUME</span><h2>Minutes of voice per day</h2></div></div>
        {stats?.daily?.length ? <CountByDay rows={stats.daily} value="minutes" format="~r" tooltip={(d) => <>
          <span>{d.minutes.toFixed(1)} min · {d.voiceovers} voiceover{d.voiceovers === 1 ? '' : 's'}{d.failed ? ` (${d.failed} failed)` : ''}</span>
          <span>{d.scripts} script write{d.scripts === 1 ? '' : 's'}</span><span>{money(d.costUsd)} Groq</span></>} /> : <Empty title="No voiceovers in this range" body="Widen the date range." />}
      </article>
      <article className="panel"><div className="panel-heading"><div><span className="eyebrow">PAID WITH</span><h2>How voice minutes were paid</h2></div></div>
        <ShareDonut slices={paidSlices} total={Math.round(paidSlices.reduce((sum, s) => sum + s.value, 0) * 10) / 10} unit="min" />
      </article>
      <article className="panel"><div className="panel-heading"><div><span className="eyebrow">VOICES</span><h2>Most used voices</h2></div></div>
        {voiceRows.length ? <BucketBars rows={voiceRows} unit="voiceovers" /> : <Empty title="No voices used yet" body="Voices appear as customers generate voiceovers." />}
      </article>
      <article className="panel"><div className="panel-heading"><div><span className="eyebrow">SCRIPT WRITER</span><h2>How script writes were paid</h2></div><small className="plain">{sc?.inputTokens || sc?.outputTokens ? `${shortNumber(sc.inputTokens)} in · ${shortNumber(sc.outputTokens)} out tokens` : ''}</small></div>
        <BucketBars rows={scriptFundingRows} color="#eb6834" unit="writes" />
      </article>
      <article className="panel"><div className="panel-heading"><div><span className="eyebrow">TONE</span><h2>Tones asked for</h2></div><small className="plain">{sc?.p50LatencyMs ? `p50 ${duration(sc.p50LatencyMs)} a script` : ''}</small></div>
        {toneRows.length ? <BucketBars rows={toneRows} color="#1baf7a" unit="writes" /> : <Empty title="No script writes yet" body="Tones appear as customers use the script writer." />}
      </article>
    </section>

    <TopUsers users={stats?.users} loading={loading} selected={customer} onSelect={setCustomer} />

    <Activity query={query} customer={customer} onClearCustomer={() => setCustomer(null)} onOpenVideos={onOpenVideos} />
  </>;
}

function TopUsers({ users, loading, selected, onSelect }) {
  return <article className={`panel table-panel voice-users ${loading ? 'is-loading' : ''}`}>
    <div className="panel-heading"><div><span className="eyebrow">WHO USES IT</span><h2>Top customers</h2></div><small className="plain">Click a customer to see their voiceovers below</small></div>
    <div className="table-wrap"><table><thead><tr><th>Customer</th><th>Plan</th><th>Voiceovers</th><th>Voice</th><th>Script writes</th><th>Credits paid</th><th>Groq cost</th><th>Last used</th></tr></thead><tbody>
      {users?.length ? users.map((u) => <tr key={u.user_id} className={selected?.id === u.user_id ? 'is-selected' : ''}>
        <td><button className="text-button video-link" onClick={() => onSelect({ id: u.user_id, email: u.email })}><b>{u.full_name || u.email}</b></button>
          <small className="plain country-line">{u.full_name ? u.email : ''}{u.country && <Flag code={u.country} />}</small></td>
        <td>{u.plan_id ? <Badge tone={u.plan_id === 'studio' ? 'info' : 'good'}>{PLAN_NAMES[u.plan_id] ?? u.plan_id}</Badge> : <Badge>Free</Badge>}</td>
        <td><b>{u.voiceovers}</b>{u.failed > 0 && <small>{u.failed} failed</small>}</td>
        <td><b>{minutes(u.seconds)}</b></td>
        <td><b>{u.scripts}</b></td>
        <td><b>{u.credits}</b></td>
        <td><b>{money(u.costUsd)}</b></td>
        <td>{ago(u.last_at)}</td>
      </tr>) : <tr><td colSpan="8"><Empty title="Nobody has used voiceovers in this range" body="Widen the date range." /></td></tr>}
    </tbody></table></div>
  </article>;
}

const EMPTY_FILTERS = { search: '', status: '', language: '', funding: '' };

/** The two activity logs — every voiceover, every script write — sharing one filter bar. */
function Activity({ query, customer, onClearCustomer, onOpenVideos }) {
  const [tab, setTab] = useState('voiceovers');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const refreshVersion = useRefreshVersion();

  useEffect(() => {
    const timer = setTimeout(() => { setApplied(filters); setPage(0); }, filters.search === applied.search ? 0 : 350);
    return () => clearTimeout(timer);
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(0); }, [customer, query, tab]);

  const path = useMemo(() => {
    const params = { ...query, limit: PAGE_SIZE, offset: page * PAGE_SIZE };
    if (applied.search.trim()) params.search = applied.search.trim();
    if (customer) params.userId = customer.id;
    if (applied.funding && !(tab === 'scripts' && applied.funding === 'mixed')) params.funding = applied.funding;
    if (tab === 'voiceovers') {
      if (applied.status) params.status = applied.status;
      if (applied.language) params.language = applied.language;
    }
    return `${tab === 'scripts' ? '/voiceovers/scripts' : '/voiceovers'}?${new URLSearchParams(params)}`;
  }, [query, page, applied, customer, tab]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError('');
    api(path).then((next) => { if (!cancelled) setData(next); })
      .catch((caught) => { if (!cancelled) setError(caught.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [path, refreshVersion]);

  const set = (key) => (event) => setFilters((f) => ({ ...f, [key]: event.target.value }));
  const total = data?.page?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const showingTab = data && (path.startsWith('/voiceovers/scripts') ? 'scripts' : 'voiceovers');

  return <>
    <section className="toolbar">
      <div className="segmented"><button className={tab === 'voiceovers' ? 'on' : ''} onClick={() => setTab('voiceovers')}>Voiceovers</button><button className={tab === 'scripts' ? 'on' : ''} onClick={() => setTab('scripts')}>Script writes</button></div>
      <input value={filters.search} onChange={set('search')} placeholder={tab === 'scripts' ? 'Search idea, name or email' : 'Search title, script, name or email'} />
      {tab === 'voiceovers' && <>
        <Select value={filters.status} onChange={set('status')} label="State" options={[['', 'All states'], ['ready', 'Ready'], ['failed', 'Failed'], ['generating', 'Generating']]} />
        <Select value={filters.language} onChange={set('language')} label="Language" options={[['', 'All languages'], ['english', 'English'], ['arabic', 'Arabic']]} />
      </>}
      <Select value={filters.funding} onChange={set('funding')} label="Payment" options={[['', 'Any payment'], ['free', 'Free weekly'], ['plan', 'Plan'], ['credits', 'Credits'], ...(tab === 'voiceovers' ? [['mixed', 'Free + credits']] : [])]} />
      {customer && <button className="subtle" onClick={onClearCustomer} title="Show every customer again">{customer.email} ✕</button>}
    </section>

    {error && <ErrorState message={error} retry={() => setApplied({ ...applied })} />}

    <article className={`panel table-panel ${loading ? 'is-loading' : ''}`}>
      <div className="panel-heading"><div><span className="eyebrow">{tab === 'scripts' ? 'SCRIPT WRITER' : 'VOICEOVERS'}</span><h2>{total} {tab === 'scripts' ? `script write${total === 1 ? '' : 's'}` : `voiceover${total === 1 ? '' : 's'}`}</h2></div>
        <div className="pager"><button className="subtle" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>← Prev</button><span>Page {page + 1} of {pages}</span><button className="subtle" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}>Next →</button></div></div>
      {showingTab === 'scripts' ? <ScriptTable items={data?.items} /> : <VoiceoverTable items={data?.items} onOpenVideos={onOpenVideos} />}
    </article>
  </>;
}

function VoiceoverTable({ items, onOpenVideos }) {
  return <div className="table-wrap"><table className="feedback-table"><thead><tr><th>Voiceover</th><th>Customer</th><th>Voice</th><th>State</th><th>Length</th><th>Paid with</th><th>Used on</th><th>Groq cost</th><th>Made</th></tr></thead><tbody>
    {items?.length ? items.map((a) => <tr key={a.id}>
      <td className="comment-cell"><b title={a.title}>{a.title}</b>
        <p title={a.script_excerpt}>{a.script_excerpt}{Number(a.script_chars) > 240 ? '…' : ''}</p>
        {a.from_script_writer && a.prompt_excerpt && <small className="plain">Idea: {a.prompt_excerpt}</small>}
        {a.deleted_at && <small className="expired-note">Deleted by customer · {date(a.deleted_at)}</small>}</td>
      <td><b>{a.user_name || a.user_email}</b><small>{a.user_name ? a.user_email : ''}</small></td>
      <td><b>{titleCase(a.voice)}</b><small>{titleCase(a.language)}</small></td>
      <td><Badge tone={STATE_TONES[a.status]}>{titleCase(a.status)}</Badge>
        <small>{a.status === 'generating' ? `${a.progress}% · ${a.chunks_done}/${a.chunks_total} pieces` : a.status === 'failed' ? (a.error || 'Failed') : a.finished_at ? `in ${duration(new Date(a.finished_at) - new Date(a.created_at))}` : ''}</small></td>
      <td><b>{a.duration_sec ? minutes(a.duration_sec) : '—'}</b><small>{shortNumber(a.chars_billed ?? a.script_chars)} chars</small></td>
      <td><FundingBadge funding={a.funding} />
        <small>{a.status === 'failed' ? 'Released — not charged'
          : a.status === 'generating' ? `Holding ${a.allowance_hold_seconds}s · ${a.credit_hold} cr`
          : [Number(a.allowance_seconds_used) > 0 && `${a.allowance_seconds_used}s from allowance`, Number(a.credits_charged) > 0 && `${a.credits_charged} credits`].filter(Boolean).join(' · ') || '—'}</small></td>
      <td>{Number(a.projects) > 0
        ? <button className="issue-link closed" title="Open the video in Videos" onClick={() => onOpenVideos(a.project_ids[0])}>{a.projects} video{Number(a.projects) > 1 ? 's' : ''} →</button>
        : <span className="unassigned">Library only</span>}</td>
      <td>{a.cost_usd != null ? <b>{money(a.cost_usd)}</b> : <span className="unassigned">—</span>}</td>
      <td>{date(a.created_at)}</td>
    </tr>) : <tr><td colSpan="9"><Empty title="No voiceovers match" body="Try clearing a filter or widening the date range." /></td></tr>}
  </tbody></table></div>;
}

function ScriptTable({ items }) {
  return <div className="table-wrap"><table className="feedback-table"><thead><tr><th>Customer</th><th>Idea</th><th>Asked for</th><th>Paid with</th><th>Output</th><th>Groq cost</th><th>When</th></tr></thead><tbody>
    {items?.length ? items.map((e) => <tr key={e.id}>
      <td><b>{e.user_name || e.user_email}</b><small>{e.user_name ? e.user_email : ''}</small></td>
      <td className="comment-cell">{e.prompt_excerpt ? <p title={e.prompt_excerpt}>{e.prompt_excerpt}</p> : <span className="unassigned">Not recorded</span>}</td>
      <td>{e.tone ? <><b>{titleCase(e.tone)}</b><small>{e.length_sec ? `${e.length_sec} s · ` : ''}{titleCase(e.language)}</small></> : <span className="unassigned">—</span>}</td>
      <td>{e.status === 'refunded' ? <Badge tone="warn">Refunded</Badge> : <FundingBadge funding={e.funding} />}<small>{Number(e.credits) > 0 ? `${e.credits} credit` : ''}</small></td>
      <td>{e.output_tokens != null ? <><b>{shortNumber(e.output_chars)} chars</b><small>{shortNumber(e.input_tokens)} in · {shortNumber(e.output_tokens)} out tokens</small></> : e.output_chars != null ? <b>{shortNumber(e.output_chars)} chars</b> : <span className="unassigned">—</span>}</td>
      <td>{e.cost_usd != null ? <b>{money(e.cost_usd)}</b> : <span className="unassigned">—</span>}</td>
      <td>{date(e.created_at)}{e.latency_ms ? <small>took {duration(e.latency_ms)}</small> : null}</td>
    </tr>) : <tr><td colSpan="7"><Empty title="No script writes match" body="Try clearing a filter or widening the date range. Idea, tone and tokens are recorded from this release on." /></td></tr>}
  </tbody></table></div>;
}
